import type { SocialProviderAccount } from "../hooks/useSocialProviderAccounts";

const CACHE_PREFIX = "social_provider_accounts:";

const storage = (): Storage | undefined => {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};

/** Old lists carry no Klap owner, so they cannot be safely migrated. */
export const discardUnownedSocialProviderAccounts = () => {
  try {
    const cache = storage();
    cache?.removeItem("social_provider_accounts");
    cache?.removeItem("socialAccounts");
  } catch {
    // Storage is optional; a denied/quota-limited cache must not block accounts.
  }
};

export const readSocialProviderAccountsCache = (
  userId: string | undefined,
): SocialProviderAccount[] | undefined => {
  if (!userId) return undefined;
  try {
    const raw = storage()?.getItem(`${CACHE_PREFIX}${userId}`);
    if (!raw) return undefined;
    const cached = JSON.parse(raw);
    // Both the key and payload carry the owner. Never accept the old bare array
    // or a list copied to another user's key as that user's initial view.
    if (
      cached?.userId !== userId ||
      !Array.isArray(cached.accounts) ||
      !cached.accounts.every(
        (account: unknown) =>
          account !== null &&
          typeof account === "object" &&
          ["id", "provider", "name", "username", "profilePicture"].every(
            (key) => typeof (account as Record<string, unknown>)[key] === "string",
          ),
      )
    ) return undefined;
    return cached.accounts;
  } catch {
    return undefined;
  }
};

export const writeSocialProviderAccountsCache = (
  userId: string,
  accounts: SocialProviderAccount[],
) => {
  if (!userId) return;
  try {
    storage()?.setItem(
      `${CACHE_PREFIX}${userId}`,
      JSON.stringify({ userId, accounts }),
    );
  } catch {
    // Fetch results remain usable when browser storage is unavailable.
  }
};

export const removeSocialProviderAccountsCache = (userId: string) => {
  try {
    storage()?.removeItem(`${CACHE_PREFIX}${userId}`);
  } catch {
    // Best effort: this cache is never shared with another login.
  }
};
